import { Fragment, useState } from "react";
import { Box, Collapse, Group, Paper, Skeleton, Stack, Text, ThemeIcon, UnstyledButton } from "@mantine/core";
import {
  IconArrowsExchange,
  IconChevronDown,
  IconCreditCard,
  IconFileText,
  IconSend,
} from "@tabler/icons-react";

/** Brand navy chevron toggle (matches card border / dashboard text). */
const TIMELINE_TOGGLE_NAVY = "#04305c";
import { useMediaQuery } from "@mantine/hooks";
import { pickCompanyDisplayName } from "@/shared/utils/companyDisplayName.util";

type TimelineStep = {
  title: string;
  done: boolean;
  completedAt: string | Date | null;
};

type TimelineSide = {
  request: TimelineStep;
  payment: TimelineStep;
  partnerReadiness: TimelineStep;
};

export type CollaborationTimelineCardItem = {
  matchId: string;
  gtid: string;
  senderGtid?: string | null;
  receiverGtid?: string | null;
  myLegalName?: string | null;
  myTradeName?: string | null;
  partnerLegalName?: string | null;
  partnerTradeName?: string | null;
  initiatedAt: string | Date;
  paymentDeadline?: string | Date | null;
  status: string;
  mySide: TimelineSide;
  partnerSide: TimelineSide;
};

type Props = {
  item: CollaborationTimelineCardItem;
};

function formatDate(value: string | Date | null): string {
  if (!value) return "Pending";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Pending";
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

function StepNode({ step, paymentDeadline }: { step: TimelineStep; paymentDeadline?: string | Date | null }) {
  const done = step.done;
  const nodeColor = done ? "var(--mantine-color-green-6)" : "var(--mantine-color-gray-5)";
  const textColor = done ? "var(--mantine-color-green-7)" : "var(--mantine-color-gray-7)";
  const bg = done ? "var(--mantine-color-green-0)" : "var(--mantine-color-gray-0)";
  const iconAndBorderColor = nodeColor;
  const stepTitle = step.title.toLowerCase();

  const StepIcon = (() => {
    if (stepTitle.includes("payment")) return IconCreditCard;
    if (stepTitle.includes("readiness") || stepTitle.includes("questionnaire")) return IconFileText;
    return IconSend;
  })();
  const isPaymentStep = stepTitle.includes("payment");
  const showPaymentDeadline = isPaymentStep && !done && !!paymentDeadline;
  const stepDateText = showPaymentDeadline
    ? `Due ${formatDate(paymentDeadline ?? null)}`
    : formatDate(step.completedAt);

  return (
    <Stack gap={6} align="center" style={{ minWidth: 72 }}>
      <ThemeIcon
        size={36}
        radius="50%"
        variant="light"
        styles={{
          root: {
            borderRadius: "50%",
            backgroundColor: bg,
            color: iconAndBorderColor,
            border: `1px solid ${iconAndBorderColor}`,
          },
        }}
      >
        <StepIcon size={18} />
      </ThemeIcon>
      <Text size="sm" fw={600} c={textColor} ta="center">
        {step.title}
      </Text>
      <Text size="xs" c={done ? "green.7" : "gray.7"} ta="center">
        {stepDateText}
      </Text>
    </Stack>
  );
}

function MobileStepIcon({ step }: { step: TimelineStep }) {
  const done = step.done;
  const nodeColor = done ? "var(--mantine-color-green-6)" : "var(--mantine-color-gray-5)";
  const bg = done ? "var(--mantine-color-green-0)" : "var(--mantine-color-gray-0)";
  const stepTitle = step.title.toLowerCase();

  const StepIcon = (() => {
    if (stepTitle.includes("payment")) return IconCreditCard;
    if (stepTitle.includes("readiness") || stepTitle.includes("questionnaire")) return IconFileText;
    return IconSend;
  })();

  return (
    <ThemeIcon
      size={34}
      radius="xl"
      variant="light"
      styles={{
        root: {
          backgroundColor: bg,
          color: nodeColor,
          border: `1px solid ${nodeColor}`,
          flexShrink: 0,
        },
      }}
      aria-label={step.title}
      title={`${step.title} • ${done ? `Done (${formatDate(step.completedAt)})` : "Pending"}`}
    >
      <StepIcon size={18} />
    </ThemeIcon>
  );
}

function MobileSide({
  laneId,
  side,
  reverse,
  paymentDeadline,
}: {
  laneId: string;
  side: TimelineSide;
  reverse?: boolean;
  paymentDeadline?: string | Date | null;
}) {
  const orderedSteps = [side.request, side.payment, side.partnerReadiness];
  const steps = reverse ? [...orderedSteps].reverse() : orderedSteps;

  return (
    <Stack gap={6}>
      <Group wrap="nowrap" gap={0} align="center" style={{ width: "100%" }}>
        {steps.map((step, idx) => {
          const hasNext = idx < steps.length - 1;
          const connectorColor = step.done ? "var(--mantine-color-green-6)" : "var(--mantine-color-gray-5)";
          return (
            <Box
              key={`${laneId}-${step.title}-${idx}`}
              style={{ position: "relative", flex: 1, display: "flex", justifyContent: "center" }}
            >
              <MobileStepIcon step={step} />
              {hasNext ? (
                <Box
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "calc(50% + 17px)",
                    right: "-50%",
                    height: 2,
                    backgroundColor: connectorColor,
                    borderRadius: 999,
                    transform: "translateY(-50%)",
                    opacity: 0.9,
                  }}
                />
              ) : null}
            </Box>
          );
        })}
      </Group>

      <Group wrap="nowrap" gap={0} align="center" style={{ width: "100%" }}>
        {steps.map((step, idx) => (
          <Box key={`${laneId}-time-${idx}`} style={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <Text size="xs" c="dimmed" ta="center" style={{ width: 48, lineHeight: 1.1 }}>
              {step.done
                ? formatDate(step.completedAt)
                : step.title.toLowerCase().includes("payment") && paymentDeadline
                  ? formatDate(paymentDeadline)
                  : "—"}
            </Text>
          </Box>
        ))}
      </Group>
    </Stack>
  );
}

function SideSteps({
  side,
  reverse,
  titleAlign,
  paymentDeadline,
  compact,
}: {
  side: TimelineSide;
  reverse?: boolean;
  titleAlign: "left" | "right";
  paymentDeadline?: string | Date | null;
  compact?: boolean;
}) {
  const orderedSteps = [side.request, side.payment, side.partnerReadiness];
  const steps = reverse ? [...orderedSteps].reverse() : orderedSteps;
  /** Short fixed connectors keep icon spacing tight (matches pre-divider layout before lanes stretched full-width). */
  const connectorWidth = compact ? 10 : 16;

  return (
    <Stack
      gap="xs"
      style={{
        flex: 1,
        width: "100%",
        alignItems: titleAlign === "left" ? "flex-start" : "flex-end",
      }}
    >
      <Group
        justify={titleAlign === "left" ? "flex-start" : "flex-end"}
        wrap="nowrap"
        gap={0}
        align="flex-start"
      >
        {steps.map((step, idx) => (
          <Fragment key={`${titleAlign}-${step.title}-${idx}`}>
            {idx > 0 ? (
              <Box
                style={{
                  width: connectorWidth,
                  height: 2,
                  alignSelf: "flex-start",
                  marginTop: 17,
                  flexShrink: 0,
                  backgroundColor: steps[idx - 1].done
                    ? "var(--mantine-color-green-6)"
                    : "var(--mantine-color-gray-5)",
                  borderRadius: 999,
                  opacity: 0.9,
                }}
              />
            ) : null}
            <StepNode step={step} paymentDeadline={paymentDeadline} />
          </Fragment>
        ))}
      </Group>
    </Stack>
  );
}

export default function CollaborationTimelineCard({ item }: Props) {
  const [expanded, setExpanded] = useState(false);
  const leftLane = { side: item.mySide, reverse: false };
  const rightLane = { side: item.partnerSide, reverse: true };
  const isMySideSender = item.mySide.request.title === "Send";
  const myDisplayName = pickCompanyDisplayName(item.myLegalName, item.myTradeName);
  const showPartnerName =
    Boolean((item.partnerLegalName ?? "").trim()) || Boolean((item.partnerTradeName ?? "").trim());
  const partnerDisplayName = showPartnerName
    ? pickCompanyDisplayName(item.partnerLegalName, item.partnerTradeName)
    : null;
  const leftRoleLabel = isMySideSender ? "Send By" : "Received By";
  const rightRoleLabel = isMySideSender ? "Received By" : "Send By";
  const rightRoleGtid = isMySideSender ? item.receiverGtid : item.senderGtid;
  const leftRoleValue = myDisplayName;
  const rightRoleValue = showPartnerName ? partnerDisplayName : rightRoleGtid || item.gtid || "—";
  const rightRoleLabelWithKind = showPartnerName ? rightRoleLabel : `${rightRoleLabel} GTID`;
  // Mantine `sm` breakpoint is a bit too small; mid-width laptops can still clip with large gaps/padding.
  const isNarrowDesktop = useMediaQuery("(max-width: 1100px)");

  const toggleExpanded = () => setExpanded((open) => !open);

  return (
    <Paper withBorder radius="md" p="sm" style={{ borderColor: "#04305c", backgroundColor: "#ebf0ff" }}>
      <UnstyledButton
        type="button"
        aria-label={expanded ? "Collapse collaboration timeline" : "Expand collaboration timeline"}
        aria-expanded={expanded}
        onClick={toggleExpanded}
        mb={expanded ? 6 : 0}
        w="100%"
        styles={{
          root: {
            display: "block",
            width: "100%",
            padding: 0,
            borderRadius: 0,
            cursor: "pointer",
            color: "inherit",
            "&:hover": { backgroundColor: "rgba(4, 48, 92, 0.06)" },
          },
        }}
      >
        <Box
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            alignItems: "center",
            columnGap: 8,
            width: "100%",
          }}
        >
          <Stack gap={0} align="flex-start" style={{ minWidth: 0 }}>
            <Text size="sm" fw={600} c="#04305c" component="span">
              {leftRoleLabel}: {leftRoleValue}
            </Text>
          </Stack>

          <Text
            size="xs"
            c="#04305c"
            ta="center"
            component="span"
            style={{ justifySelf: "center", whiteSpace: "nowrap" }}
          >
            Initiated on: {formatDate(item.initiatedAt)}
          </Text>

          <Group gap={4} align="flex-start" wrap="nowrap" style={{ justifySelf: "end", minWidth: 0 }}>
            <Stack gap={0} align="flex-end" style={{ minWidth: 0 }}>
              <Text size="sm" fw={600} c="#04305c" ta="right" component="span">
                {rightRoleLabelWithKind}: {rightRoleValue}
              </Text>
            </Stack>
            <Box
              component="span"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                flexShrink: 0,
                marginTop: 2,
                borderRadius: "50%",
                border: `2.5px solid ${TIMELINE_TOGGLE_NAVY}`,
              }}
            >
              <IconChevronDown
                size={16}
                color={TIMELINE_TOGGLE_NAVY}
                stroke={2.5}
                style={{
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 200ms ease-in-out",
                }}
              />
            </Box>
          </Group>
        </Box>
      </UnstyledButton>

      <Collapse in={expanded}>
      {/* Mobile: stacked, readable timeline */}
      <Box hiddenFrom="sm">
        <Stack gap="md">
          <MobileSide
            laneId="my"
            side={leftLane.side}
            reverse={leftLane.reverse}
            paymentDeadline={item.paymentDeadline}
          />
          <Group justify="center" align="center">
            <ThemeIcon size={30} radius="50%" variant="light" color="blue">
              <IconArrowsExchange size={16} />
            </ThemeIcon>
          </Group>
          <MobileSide
            laneId="partner"
            side={rightLane.side}
            reverse={rightLane.reverse}
            paymentDeadline={item.paymentDeadline}
          />
        </Stack>
      </Box>

      {/* Desktop/tablet: horizontal lanes with center divider */}
      <Group visibleFrom="sm" wrap="nowrap" align="stretch" gap={0}>
        <Box style={{ flex: 1, minWidth: 0, paddingRight: isNarrowDesktop ? 10 : 16 }}>
          <SideSteps
            side={leftLane.side}
            reverse={leftLane.reverse}
            titleAlign="left"
            paymentDeadline={item.paymentDeadline}
            compact={isNarrowDesktop}
          />
        </Box>
        <Box
          aria-hidden
          style={{
            width: 2,
            flexShrink: 0,
            alignSelf: "stretch",
            marginBlock: 6,
            marginInline: isNarrowDesktop ? 10 : 16,
            borderRadius: 2,
            background:
              "linear-gradient(180deg, transparent 0%, rgba(4, 48, 92, 0.22) 8%, rgba(4, 48, 92, 0.42) 50%, rgba(4, 48, 92, 0.22) 92%, transparent 100%)",
          }}
        />
        <Box style={{ flex: 1, minWidth: 0, paddingLeft: isNarrowDesktop ? 10 : 16 }}>
          <SideSteps
            side={rightLane.side}
            reverse={rightLane.reverse}
            titleAlign="right"
            paymentDeadline={item.paymentDeadline}
            compact={isNarrowDesktop}
          />
        </Box>
      </Group>
      </Collapse>
    </Paper>
  );
}

function TimelineSkeletonLane({ titleAlign }: { titleAlign: "left" | "right" }) {
  const connectorWidth = 16;

  return (
    <Stack
      gap="xs"
      style={{
        flex: 1,
        width: "100%",
        alignItems: titleAlign === "left" ? "flex-start" : "flex-end",
      }}
    >
      <Skeleton
        height={12}
        width={90}
        radius="sm"
        style={{ alignSelf: titleAlign === "left" ? "flex-start" : "flex-end" }}
      />
      <Group justify={titleAlign === "left" ? "flex-start" : "flex-end"} wrap="nowrap" gap={0} align="flex-start">
        {Array.from({ length: 3 }).map((_, idx) => (
          <Fragment key={`timeline-skeleton-node-${idx}`}>
            {idx > 0 ? (
              <Skeleton
                height={2}
                width={connectorWidth}
                radius="xl"
                style={{ alignSelf: "flex-start", marginTop: 17, flexShrink: 0 }}
              />
            ) : null}
            <Stack gap={6} align="center" style={{ minWidth: 72 }}>
              <Skeleton height={36} width={36} radius="xl" />
              <Skeleton height={10} width={56} radius="sm" />
              <Skeleton height={10} width={48} radius="sm" />
            </Stack>
          </Fragment>
        ))}
      </Group>
    </Stack>
  );
}

export function CollaborationTimelineCardSkeleton() {
  return (
    <Paper withBorder radius="md" p="md" style={{ borderColor: "#04305c", backgroundColor: "#ebf0ff" }}>
      <Group justify="space-between" align="center" mb="sm" wrap="nowrap">
        <Stack gap={4}>
          <Skeleton height={10} width={72} radius="sm" />
          <Skeleton height={14} width={96} radius="sm" />
        </Stack>
        <Skeleton height={12} width={140} radius="sm" />
        <Stack gap={4} align="flex-end">
          <Skeleton height={10} width={78} radius="sm" />
          <Skeleton height={14} width={96} radius="sm" />
        </Stack>
      </Group>

      <Group wrap="nowrap" align="stretch" gap={0}>
        <Box style={{ flex: 1, minWidth: 0, paddingRight: 16 }}>
          <TimelineSkeletonLane titleAlign="left" />
        </Box>
        <Box
          aria-hidden
          style={{
            width: 2,
            flexShrink: 0,
            alignSelf: "stretch",
            marginBlock: 6,
            marginInline: 16,
            borderRadius: 2,
            background:
              "linear-gradient(180deg, transparent 0%, rgba(4, 48, 92, 0.22) 8%, rgba(4, 48, 92, 0.42) 50%, rgba(4, 48, 92, 0.22) 92%, transparent 100%)",
          }}
        />
        <Box style={{ flex: 1, minWidth: 0, paddingLeft: 16 }}>
          <TimelineSkeletonLane titleAlign="right" />
        </Box>
      </Group>
    </Paper>
  );
}
